/**
 * One offer, once the portal is installed and open: turn on notifications for this
 * device, the way a phone app asks on first launch. It has to be a button — browsers
 * only show the permission dialog in answer to a tap.
 *
 * Installing itself is the browser's own prompt, not ours (see lib/install.ts).
 *
 * "Not now" is remembered for two weeks on this device: an offer that comes back on
 * every page is how people learn to ignore it.
 */

import { useEffect, useState } from 'react';

import * as webPush from '@/features/notifications/webPush';
import { useI18n } from '@/i18n';
import { isStandalone } from '@/lib/install';

const KEY = 'hh.nudge.push';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

export function AppNudges() {
  const { t } = useI18n();
  const [state, setState] = useState<webPush.PushStatus | null>(null);
  const [hidden, setHidden] = useState(snoozed);
  const standalone = isStandalone();

  useEffect(() => {
    if (!standalone || hidden) return;
    webPush.status().then(setState).catch(() => setState('unsupported'));
  }, [standalone, hidden]);

  if (!standalone || hidden || state !== 'off') return null;

  const dismiss = () => {
    try {
      localStorage.setItem(KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* storage blocked: it asks again next time */
    }
    setHidden(true);
  };

  return (
    <div className="shrink-0 border-b border-border bg-surface-alt px-4 py-2.5">
      <div className="mx-auto flex max-w-[1400px] items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{t('nudge.pushTitle')}</p>
          <p className="text-xs text-muted leading-snug mt-0.5">{t('nudge.pushText')}</p>
        </div>
        <button
          type="button"
          className="btn-primary shrink-0"
          onClick={() => {
            webPush
              .turnOn()
              .then(setState)
              .catch(() => undefined);
          }}
        >
          {t('nudge.pushOn')}
        </button>
        <button
          type="button"
          aria-label={t('nudge.dismiss')}
          title={t('nudge.dismiss')}
          className="shrink-0 rounded p-1 text-subtle hover:text-text"
          onClick={dismiss}
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M5 5l10 10M15 5 5 15" />
          </svg>
        </button>
      </div>
    </div>
  );
}
